<template>
  <div class="tq-person-selector-tree" v-loading="treeLoading">
    <!-- 上下结构 -->
    <div class="tq-person-selector-tree-search">
      <tq-search-input
        v-model="modelValue"
        v-bind="inputProps"
        border
      ></tq-search-input>
    </div>
    <div class="tq-person-selector-tree-content">
      <el-scrollbar height="480px">
        <el-tree
          ref="treeRef"
          empty-text=""
          :data="treeData"
          :highlight-current="true"
          :filter-node-method="filterDeptList"
          @node-click="onNodeClick"
        >
          <template #empty>
            <Empty
              v-if="treeData.length"
              :top="162"
              unit="px"
              text="暂无结果，请重新输入"
            ></Empty>
          </template>
        </el-tree>
      </el-scrollbar>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  ref,
  watch,
  inject,
  onBeforeMount,
  onBeforeUnmount,
} from "vue";
import type {WatchStopHandle} from "vue";
import type { PersonSelector } from "../types/index";
import { ElMessage, ElTree } from "element-plus";
import { getAllDept, getAllDeptPersons } from "../apis/index";
import { onPersonListChange, onLoadingChange } from "../hooks/index";
import Empty from "./Empty.vue";

const defaultDepartmentId = inject<string>("defaultDepartmentId");

const modelValue = ref<string>("");
const treeRef = ref<InstanceType<typeof ElTree>>();
const treeLoading = ref<boolean>(false);
const treeData = ref<PersonSelector.Tree[]>([]);
const watchList = ref<WatchStopHandle[]>([]);
const rootDept = ref<PersonSelector.DeptList>();

const inputProps = {
  width: "100%",
  backgroundColor: "transparent",
  borderRadius: "3px",
  placeholder: "输入部门搜索",
};

const findTargetNode = (id: string, arr: PersonSelector.DeptList[]): void => {
  arr.forEach(async (item) => {
    if (item.deptId === id) {
      rootDept.value = item;
    } else if (item.deptList?.length) {
      findTargetNode(id, item.deptList);
    }
  });
};

onBeforeMount(() => {
  treeLoading.value = true;
  getAllDept()
    .then(async ({ responseData }:any) => {
      let result: PersonSelector.DeptList = responseData.find(
        (item:any) => item.departCode === "00000001"
      )!;
      if (defaultDepartmentId) {
        findTargetNode(defaultDepartmentId, [result]);
        result = rootDept.value ?? result;
        !rootDept.value && ElMessage.error("根部门id不存在");
      }
      getPersonByDeptId(result.deptId);
      treeData.value = formatDeptList(result.deptList);
    })
    .catch((err:any) => {
      ElMessage.error(err.message ?? err);
    })
    .finally(() => {
      treeLoading.value = false;
    });
});

watchList.value.push(
  watch(modelValue, (keyword) => treeRef.value!.filter(keyword))
);
onBeforeUnmount(() => {
  watchList.value.forEach((unwacth) => unwacth());
});

const onNodeClick = async (nodeData: PersonSelector.Tree) => {
  if (!nodeData.isLoad) {
    nodeData.isLoad = true;
    nodeData.personList.push(...(await getPersonByDeptId(nodeData.deptId)));
  } else {
    onPersonListChange(nodeData.personList);
  }
};

const getPersonByDeptId = (
  deptId: string
): Promise<PersonSelector.PersonList[]> =>
  new Promise(async (resolve, reject) => {
    onLoadingChange(true);
    try {
      const { responseData } = await getAllDeptPersons(deptId);
      onPersonListChange(responseData.personnelList);
      resolve(responseData.personnelList);
    } catch (err: any) {
      ElMessage.error(err.message ?? err);
      reject(err);
    } finally {
      onLoadingChange(false);
    }
  });

const filterDeptList = (value: string, data: { [key: string]: any }) => {
  if (!value) return true;
  return data.label.includes(value);
};
const formatDeptList = (
  list: PersonSelector.DeptList[]
): PersonSelector.Tree[] => {
  return list.map(({ deptName, deptId, deptList }) => {
    const tree: PersonSelector.Tree = {
      label: deptName,
      deptId,
      isLoad: false,
      personList: [],
    };
    if (deptList.length) {
      tree.children = formatDeptList(deptList);
    }
    return tree;
  });
};
</script>